import { ref, watch, onMounted, onUnmounted, getCurrentInstance } from 'vue';

export const ADMIN_SIDEBAR_STORAGE_KEY = 'admin.sidebar.collapsed';
export const ADMIN_SIDEBAR_MOBILE_BREAKPOINT = 1024;
export const ADMIN_SIDEBAR_LABELS_DELAY = 200;

/**
 * Shared module-scope state: the header trigger and the sidebar itself must
 * observe the exact same collapse/mobile state without prop-drilling.
 */
const collapsed = ref(false);
const isMobile = ref(false);
const mobileOpen = ref(false);
const labelsVisible = ref(true);
let labelsTimer = null;
let initialized = false;

watch(collapsed, value => {
    if (labelsTimer) {
        clearTimeout(labelsTimer);
        labelsTimer = null;
    }
    if (value) {
        labelsVisible.value = false;
        return;
    }
    labelsTimer = setTimeout(() => {
        labelsVisible.value = true;
        labelsTimer = null;
    }, ADMIN_SIDEBAR_LABELS_DELAY);
}, { flush: 'sync' });


function readPersistedCollapsed() {
    try {
        return localStorage.getItem(ADMIN_SIDEBAR_STORAGE_KEY) === '1';
    } catch (error) {
        return false;
    }
}

function persistCollapsed() {
    try {
        localStorage.setItem(ADMIN_SIDEBAR_STORAGE_KEY, collapsed.value ? '1' : '0');
    } catch (error) {
        // localStorage no disponible: la preferencia no se persiste.
    }
}

/**
 * Recompute the mobile flag from the viewport width. Closing the mobile
 * drawer when leaving mobile avoids a stuck off-canvas state on resize.
 */
export function syncAdminSidebarViewport() {
    const width = typeof window !== 'undefined' ? window.innerWidth : ADMIN_SIDEBAR_MOBILE_BREAKPOINT;
    isMobile.value = width < ADMIN_SIDEBAR_MOBILE_BREAKPOINT;
    if (!isMobile.value) {
        mobileOpen.value = false;
    }
}

/**
 * Reset the shared state (test helper).
 */
export function resetAdminSidebarState() {
    if (labelsTimer) {
        clearTimeout(labelsTimer);
        labelsTimer = null;
    }
    collapsed.value = false;
    isMobile.value = false;
    mobileOpen.value = false;
    labelsVisible.value = true;
    initialized = false;
}

/**
 * Admin shell sidebar state: icon-rail collapse on desktop (persisted),
 * off-canvas drawer on mobile.
 */
export function useAdminSidebar() {
    if (!initialized) {
        initialized = true;
        collapsed.value = readPersistedCollapsed();
        labelsVisible.value = !collapsed.value;
        syncAdminSidebarViewport();
    }

    function toggle() {
        if (isMobile.value) {
            mobileOpen.value = !mobileOpen.value;
            return;
        }
        collapsed.value = !collapsed.value;
        persistCollapsed();
    }

    function closeMobile() {
        mobileOpen.value = false;
    }

    function expand() {
        if (collapsed.value) {
            collapsed.value = false;
            persistCollapsed();
        }
    }

    function setCollapsed(value) {
        collapsed.value = !!value;
        persistCollapsed();
    }

    if (getCurrentInstance()) {
        onMounted(() => {
            syncAdminSidebarViewport();
            window.addEventListener('resize', syncAdminSidebarViewport);
        });
        onUnmounted(() => {
            window.removeEventListener('resize', syncAdminSidebarViewport);
        });
    }

    return { collapsed, isMobile, mobileOpen, labelsVisible, toggle, closeMobile, expand, setCollapsed };
}
